import React from 'react';
import { ArrowLeft, CheckCircle, AlertTriangle, Info, HelpCircle, DollarSign, Route, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { analyticsService } from '../services/analyticsService';
import TruckLogo from '../components/TruckLogo';
import BusinessFooter from '../components/BusinessFooter';

const FormGuidePage: React.FC = () => {
  const { t } = useLanguage();

  // Track page view when component mounts
  React.useEffect(() => {
    analyticsService.trackPageView('form_guide_page');
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <Link 
              to="/"
              className="mr-4 p-2 rounded-full hover:bg-primary-dark transition-colors"
              aria-label={t('common.back')}
            >
              <ArrowLeft size={24} />
            </Link>
            <div className="flex items-center">
              <TruckLogo size={28} className="text-white mr-2" />
              <h1 className="text-xl font-bold uppercase">COTIZATRIN</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center">
            <HelpCircle className="mr-2 text-orange-500" size={28} />
            {t('formGuide.title', 'Form Completion Guide for Quotation')}
          </h1>
          
          <div className="prose max-w-none text-sm sm:text-base">
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h2 className="text-lg sm:text-xl font-semibold text-blue-800 mb-2 flex items-center">
                <Info className="mr-2" size={20} />
                {t('formGuide.introduction.title', 'Introduction')}
              </h2>
              <p className="text-blue-700">
                {t('formGuide.introduction.content', 'This guide will help you complete the quotation form correctly to get the most accurate cost for your freight transportation. Follow the steps below for a smooth experience.')}
              </p>
            </div>

            {/* Basic vs Advanced Mode Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">1</span>
                {t('formGuide.basicMode.title', 'Basic Mode vs. Advanced Mode')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <p className="mb-3">{t('formGuide.basicMode.content', 'The form has two operation modes:')}</p>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-2">
                    <span className="font-semibold text-green-600">{t('formGuide.basicMode.basic', 'Basic Mode:')}</span> {t('formGuide.basicMode.basic', 'Includes vehicle configuration, fuel price, and locations. Ideal for quick quotations.')}
                  </li>
                  <li className="mb-2">
                    <span className="font-semibold text-blue-600">{t('formGuide.basicMode.advanced', 'Advanced Mode:')}</span> {t('formGuide.basicMode.advanced', 'Adds cargo type, custom cost parameters, and currency selection. Recommended for more precise and professional quotations.')}
                  </li>
                </ul>
                <p className="text-sm text-gray-600 italic">
                  <strong>{t('formGuide.basicMode.howToEnable', 'How to Enable:')}</strong> {t('formGuide.basicMode.howToEnable', 'To activate Advanced Mode, click the "Advanced" button in the top navigation bar.')}
                </p>
              </div>
            </div>

            {/* Vehicle Selection Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">2</span>
                {t('formGuide.vehicleSelection.title', 'Vehicle Selection')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <p className="mb-3">{t('formGuide.vehicleSelection.description', 'Vehicle configuration is fundamental for obtaining an accurate quotation:')}</p>
                <h4 className="font-semibold text-gray-800 mb-2">{t('formGuide.vehicleSelection.vehicleTypes', 'Available Vehicle Types:')}</h4>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.vehicleSelection.pickupTruck', 'Pickup Trucks: For light loads (1.5-3.5 tons)')}</li>
                  <li className="mb-1">{t('formGuide.vehicleSelection.mediumTrucks', 'Rabon and Torton: For medium loads (10-15 tons)')}</li>
                  <li className="mb-1">{t('formGuide.vehicleSelection.tractorTrailers', 'Tractor-Trailers: For heavy loads (25-35 tons)')}</li>
                  <li className="mb-1">{t('formGuide.vehicleSelection.fullUnits', 'Full Units: For very heavy and high-volume loads (40+ tons)')}</li>
                </ul>
                <h4 className="font-semibold text-gray-800 mb-2">{t('formGuide.vehicleSelection.trailerSelection', 'Trailer Selection:')}</h4>
                <p className="text-sm text-gray-600 mb-2">{t('formGuide.vehicleSelection.trailerNote', 'Only appears for vehicles that require a trailer (tractor-trailers and full units)')}</p>
                <p className="text-sm text-gray-600 italic">{t('formGuide.vehicleSelection.visualSelection', 'Use the visual icons to easily identify the type of vehicle and trailer you need.')}</p>
              </div>
            </div>

            {/* Fuel Price Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">3</span>
                {t('formGuide.fuelPrice.title', 'Fuel Price')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <p className="mb-3">{t('formGuide.fuelPrice.description', 'This section is always visible and fundamental for calculation:')}</p>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.fuelPrice.currentPrice', 'The system shows an updated default price')}</li>
                  <li className="mb-1">{t('formGuide.fuelPrice.customPrice', 'You can adjust the price if you have more precise information from your supplier')}</li>
                </ul>
                <p className="text-sm text-gray-600 italic">{t('formGuide.fuelPrice.impactNote', 'Fuel price directly affects the total transportation cost')}</p>
              </div>
            </div>

            {/* Location Selection Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">4</span>
                {t('formGuide.locationSelection.title', 'Location Selection')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.locationSelection.originDestination', 'Origin and Destination')}</h3>
                <p className="mb-3">{t('formGuide.locationSelection.enterLocations', 'Enter the exact locations for the origin and destination of your freight:')}</p>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.locationSelection.option1', 'Type the complete address')}</li>
                  <li className="mb-1">{t('formGuide.locationSelection.option2', 'Select from autocomplete suggestions')}</li>
                  <li className="mb-1">{t('formGuide.locationSelection.option3', 'Locate the point on the interactive map')}</li>
                </ul>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-amber-800 font-medium text-sm">
                    {t('formGuide.locationSelection.addressHelper', 'IMPORTANT: Include the most complete address possible (street and number, neighborhood, city, state, postal code) to avoid incorrect locations')}
                  </p>
                </div>
                
                <h4 className="font-semibold text-gray-800 mb-2">{t('formGuide.locationSelection.mapInteraction', 'Map Interaction:')}</h4>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.locationSelection.dragMarkers', 'You can drag the markers to adjust the exact position')}</li>
                  <li className="mb-1">{t('formGuide.locationSelection.mapCalculation', 'The map will automatically calculate the route between selected points')}</li>
                  <li className="mb-1">{t('formGuide.locationSelection.clearMap', 'Use the "Clear Map" button to start over')}</li>
                </ul>
                <p className="text-sm text-gray-600 italic">
                  {t('formGuide.locationSelection.routeValidation', 'Verify that the route shown on the map is correct before generating the quotation')}
                </p>
              </div>
            </div>

            {/* Additional Destinations Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">5</span>
                {t('formGuide.intermediateStops.title', 'Additional Destinations')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.intermediateStops.howToEnable', 'How to Enable:')}</h3>
                <p className="mb-3 text-blue-700">{t('formGuide.intermediateStops.enableStep', 'Check the "Enable Additional Destinations" checkbox to activate this functionality')}</p>
                
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.intermediateStops.description', 'Once enabled, you can add up to 5 additional destinations for complex routes:')}</h3>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.intermediateStops.addingDestinations', 'Adding Destinations:')}</h4>
                  <ul className="list-disc pl-5 mb-3">
                    <li className="mb-1">{t('formGuide.intermediateStops.step1', 'Click "Add destination" to add a new location')}</li>
                    <li className="mb-1">{t('formGuide.intermediateStops.step2', 'Enter the complete address of the destination')}</li>
                    <li className="mb-1">{t('formGuide.intermediateStops.step3', 'Configure the load status for each segment (loaded/empty)')}</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.intermediateStops.loadStatus', 'Load Status:')}</h4>
                  <ul className="list-disc pl-5 mb-3">
                    <li className="mb-1"><span className="font-semibold text-green-600">Cargado:</span> {t('formGuide.intermediateStops.loadedDesc', 'The vehicle transports cargo in that segment')}</li>
                    <li className="mb-1"><span className="font-semibold text-amber-600">Vacío:</span> {t('formGuide.intermediateStops.emptyDesc', 'The vehicle travels without cargo (lower fuel consumption)')}</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.intermediateStops.routeSequence', 'Route Sequence:')}</h4>
                  <ul className="list-disc pl-5 mb-3">
                    <li className="mb-1">{t('formGuide.intermediateStops.routeOrder', 'The route is calculated in order: Origin → Main Destination → Additional Destination 1 → Additional Destination 2, etc.')}</li>
                    <li className="mb-1">{t('formGuide.intermediateStops.optimization', 'The system does NOT automatically reorder to optimize the route')}</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.intermediateStops.costCalculation', 'Cost Calculation:')}</h4>
                  <p className="text-gray-600 mb-2">{t('formGuide.intermediateStops.segmentCosts', 'Each segment is calculated individually according to its load status')}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.intermediateStops.limitations', 'Important Considerations:')}</h4>
                  <ul className="list-disc pl-5 mb-3">
                    <li className="mb-1">{t('formGuide.intermediateStops.tollAccuracy', 'Many destinations may affect the accuracy of toll calculations')}</li>
                    <li className="mb-1">{t('formGuide.intermediateStops.routeComplexity', 'Very complex routes may take longer to process')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Advanced Mode Features Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">6</span>
                {t('formGuide.advancedMode.title', 'Advanced Mode Features')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <p className="mb-3">{t('formGuide.advancedMode.description', 'Advanced Mode adds professional options for more precise quotations:')}</p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <h4 className="font-semibold text-blue-800 mb-2">{t('formGuide.advancedMode.howToEnable', 'How to Activate:')}</h4>
                  <p className="text-blue-700 text-sm">{t('formGuide.advancedMode.enableButton', 'Click the "Advanced" button in the top navigation bar')}</p>
                </div>
                
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.advancedMode.sections', 'Additional Sections:')}</h3>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.advancedMode.cargoType', 'Cargo Type:')}</h4>
                  <p className="text-gray-600 mb-2">{t('formGuide.advancedMode.cargoTypeDesc', 'Select the specific type of cargo you will transport. This affects the risk factor and security recommendations.')}</p>
                  <h5 className="font-medium text-gray-700 mb-1">{t('formGuide.advancedMode.riskFactors', 'Risk Factors:')}</h5>
                  <ul className="list-disc pl-5 mb-3">
                    <li className="mb-1">{t('formGuide.advancedMode.riskGeneral', 'General Merchandise: Standard factor (1.0x)')}</li>
                    <li className="mb-1">{t('formGuide.advancedMode.riskPerishable', 'Perishable Food: High factor (1.3x)')}</li>
                    <li className="mb-1">{t('formGuide.advancedMode.riskElectronics', 'Electronics: Very high factor (1.4x)')}</li>
                    <li className="mb-1">{t('formGuide.advancedMode.riskHazardous', 'Hazardous Materials: Extreme factor (1.8x)')}</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.advancedMode.costParameters', 'Cost Parameters:')}</h4>
                  <ul className="list-disc pl-5 mb-3">
                    <li className="mb-1">{t('formGuide.advancedMode.profitMargin', 'Profit Margin: Define the percentage of profit over the base cost')}</li>
                    <li className="mb-1">{t('formGuide.advancedMode.vatTax', 'VAT/Tax: Tax percentage applied to toll costs')}</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">{t('formGuide.advancedMode.currencySelection', 'Currency Selection:')}</h4>
                  <p className="text-gray-600 mb-2">{t('formGuide.advancedMode.currencyDesc', 'Choose the currency in which you want to see all results (MXN, USD, EUR, etc.)')}</p>
                  <p className="text-sm text-gray-600 italic">{t('formGuide.advancedMode.conversionNote', 'All costs will be automatically converted to the selected currency')}</p>
                </div>
              </div>
            </div>

            {/* Quotation Generation Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center mr-2">7</span>
                {t('formGuide.generatingQuotation.title', 'Generating Quotation')}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.generatingQuotation.requirements', 'Requirements to Generate:')}</h3>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.generatingQuotation.reqVehicle', 'Select vehicle type (and trailer if applicable)')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.reqLocations', 'Enter valid origin and destination')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.reqRoute', 'Calculate the route on the map')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.reqTokens', 'Have at least 1 quotation credit available')}</li>
                </ul>
                
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.generatingQuotation.reviewGenerate', 'Generation Process:')}</h3>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.generatingQuotation.step1', 'Review that the information is correct')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.step2', 'Click the green "Generate Quotation" button')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.step3', 'Confirm that you want to use 1 credit for this calculation')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.step4', 'Wait while the information is processed and tolls are calculated')}</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg mb-4 border-l-4 border-green-500">
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.generatingQuotation.automaticSave', 'Automatic Save:')}</h3>
                <p className="text-green-700">{t('formGuide.generatingQuotation.autoSaveDesc', 'The quotation is automatically saved to your history after generation')}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-primary">
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.generatingQuotation.resultIncludes', 'The generated quotation will include:')}</h3>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.generatingQuotation.detailedBreakdown', 'Detailed cost breakdown in the selected currency')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.tollInfo', 'Toll booth information with converted costs')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.securityRecommendations', 'Security recommendations based on cargo type (advanced mode)')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.routeMap', 'Route map')}</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4 border-l-4 border-green-500">
                <h3 className="font-semibold text-gray-800 mb-2">{t('formGuide.generatingQuotation.afterGenerate', 'After Generating')}</h3>
                <p className="mb-3">{t('formGuide.generatingQuotation.afterGenerateDesc', 'Once the quotation is generated, you can:')}</p>
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-1">{t('formGuide.generatingQuotation.newQuotation', 'Create a "New Quotation" with the blue button')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.changeCurrency', 'Change the currency to view costs in different denominations')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.downloadPDF', 'Download the quotation in PDF format (it will be generated in the selected currency)')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.downloadMap', 'Download only the route map image')}</li>
                  <li className="mb-1">{t('formGuide.generatingQuotation.viewHistory', 'View quotation history in the corresponding tab')}</li>
                </ul>
                
                <div className="flex items-start mb-4">
                  <Info className="text-blue-500 mt-1 mr-2 flex-shrink-0" size={18} />
                  <p className="text-gray-700">
                    {t('formGuide.generatingQuotation.tokenNote', 'Each generated quotation consumes one token. If you need to create multiple variants, consider acquiring an additional token package.')}
                  </p>
                </div>
              </div>
            </div>

            {/* Tips Section */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-4 flex items-center">
                <span className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center mr-2">💡</span>
                {t('formGuide.tips.title', 'Tips for Better Results')}
              </h2>
              
              <div className="bg-green-50 p-4 rounded-lg mb-4 border-l-4 border-green-500">
                <ul className="list-disc pl-5 mb-3">
                  <li className="mb-2"><strong>📍</strong> {t('formGuide.tips.tip1', 'Use complete addresses with postal code for greater accuracy')}</li>
                  <li className="mb-2"><strong>🗺️</strong> {t('formGuide.tips.tip2', 'Verify that the route on the map is correct before generating')}</li>
                  <li className="mb-2"><strong>⚙️</strong> {t('formGuide.tips.tip3', 'Advanced mode provides more professional and detailed quotations')}</li>
                  <li className="mb-2"><strong>🚛</strong> {t('formGuide.tips.tip4', 'Load statuses (loaded/empty) significantly affect fuel costs')}</li>
                  <li className="mb-2"><strong>💾</strong> {t('formGuide.tips.tip5', 'Save important quotations by downloading the PDF')}</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h2 className="text-lg sm:text-xl font-semibold text-orange-800 mb-2 flex items-center">
                <HelpCircle className="mr-2" size={20} />
                {t('formGuide.additionalHelp.title', 'Need Additional Help?')}
              </h2>
              <p className="text-orange-700 mb-3">
                {t('formGuide.additionalHelp.content', 'If you have specific questions about how to complete any part of the form or need personalized assistance, contact our support team:')}
              </p>
              <p className="text-orange-800 font-semibold">
                soporte@cotizatrin.com
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mb-8">
          <Link
            to="/"
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 flex items-center"
          >
            <ArrowLeft className="mr-2" />
            {t('formGuide.returnToForm', 'Return to Form')}
          </Link>
        </div>
      </main>

      <BusinessFooter variant="minimal" />
    </div>
  );
};

export default FormGuidePage;